import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { storage } from '@good-season/platform';
import { ChangelogSchema, type ChangelogEntry } from '@good-season/schema';
import { Button, Dialog } from '@good-season/ui';

import { api } from '../api/client.js';
import {
  UPDATE_ARMED_KEY,
  WHATS_NEW_VERSION_KEY,
  bulletsFor,
  isScorebookPath,
  rolesFromMemberships,
  visibleEntries,
} from './whats-new.js';

/**
 * After a Reload, once, a short note of what changed.
 *
 * Armed by the update strip just before it applies the waiting worker. This
 * boot reads that flag. The change log is fetched, never bundled, and a
 * failure (including no signal) leaves the flag set and shows nothing: the
 * note can appear on a later boot. Entries the person's roles should not see
 * are left out. The live scorebook never hosts it.
 *
 * Closing, Escape, Got it, and See more all count as having seen this version,
 * so the note does not return for the same newest entry.
 */
export function WhatsNewDialog() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const deferred = isScorebookPath(pathname);
  const [bullets, setBullets] = useState<readonly ChangelogEntry[] | null>(null);

  useEffect(() => {
    if (deferred) {
      setBullets(null);
      return;
    }
    if (storage.readValue(UPDATE_ARMED_KEY) !== '1') return;
    let cancelled = false;

    void (async () => {
      try {
        const log = await api<{ entries?: unknown }>(
          '/api/changelog',
          { cache: 'no-store' },
          { timeoutMs: 10000 },
        );
        const memberships = await api<unknown>(
          '/api/team/memberships',
          { cache: 'no-store' },
          { timeoutMs: 10000 },
        ).catch(() => ({ memberships: [] }));
        if (cancelled) return;

        const parsed = ChangelogSchema.safeParse(log.entries);
        if (!parsed.success) return;

        const visible = visibleEntries(parsed.data, rolesFromMemberships(memberships));
        const next = bulletsFor(visible, storage.readValue(WHATS_NEW_VERSION_KEY));
        if (next.length === 0) {
          const tip = visible[0]?.id;
          if (tip !== undefined) storage.writeValue(WHATS_NEW_VERSION_KEY, tip);
          storage.writeValue(UPDATE_ARMED_KEY, '0');
          return;
        }
        setBullets(next);
      } catch {
        // Keep the flag. No signal, or a signed-out boot, says nothing.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [deferred, pathname]);

  function acknowledge(): void {
    const tip = bullets?.[0]?.id;
    if (tip !== undefined) storage.writeValue(WHATS_NEW_VERSION_KEY, tip);
    storage.writeValue(UPDATE_ARMED_KEY, '0');
    setBullets(null);
  }

  function seeMore(): void {
    acknowledge();
    const team = new URLSearchParams(search).get('team');
    void navigate(
      team === null || team === ''
        ? '/changelog'
        : { pathname: '/changelog', search: `?${new URLSearchParams({ team }).toString()}` },
    );
  }

  return (
    <Dialog
      open={bullets !== null}
      onOpenChange={(open) => {
        if (!open) acknowledge();
      }}
      title="What's new"
      description="A few things that changed in this version."
      footer={
        <>
          <Button tone="ghost" onClick={seeMore}>
            See more
          </Button>
          <Button onClick={acknowledge}>Got it</Button>
        </>
      }
    >
      <ul className="whatsNew__list">
        {bullets?.map((entry) => (
          <li key={entry.id}>{entry.title}</li>
        ))}
      </ul>
    </Dialog>
  );
}
